<template>
  <div ref="rootRef" class="form__input">
    <div
      class="select-multiple"
      :class="{
        'select-multiple--errors': errors?.length,
        'select-multiple--disabled': disabled
      }"
    >
        <label
          v-if="label"
          :class="{ 'label--required': required }"
          class="label label--muted label--semibold"
        >
          {{ label }}
        </label>
      <div class="select-multiple__inner">
        <span
          class="select-multiple__tag tag"
          v-for="option in filteredSelectedOptions"
          :key="option.value"
          @mousedown="onRemoveOption(option.value)"
        >
          {{ option.text }}
          <svg class="select-multiple__tag-icon" width="8" height="8" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.646447 1.14645C0.841709 0.951184 1.15829 0.951184 1.35355 1.14645L4 3.79289L6.64645 1.14645C6.84171 0.951184 7.15829 0.951184 7.35355 1.14645C7.54882 1.34171 7.54882 1.65829 7.35355 1.85355L4.70711 4.5L7.35355 7.14645C7.54882 7.34171 7.54882 7.65829 7.35355 7.85355C7.15829 8.04882 6.84171 8.04882 6.64645 7.85355L4 5.20711L1.35355 7.85355C1.15829 8.04882 0.841709 8.04882 0.646447 7.85355C0.451184 7.65829 0.451184 7.34171 0.646447 7.14645L3.29289 4.5L0.646447 1.85355C0.451184 1.65829 0.451184 1.34171 0.646447 1.14645Z" fill="currentColor" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <div class="select-multiple__search-wrapper" :class="{'select-multiple__search-wrapper--focused': isOpenDropdown }">
          <input
            class="select-multiple__search-field"
            ref="searchRef"
            v-model.trim="search"
            @input="onSearchInput"
            @focus="onFocus"
            @blur="onBlur"
            @mousedown="onFieldMousedown"
          />
        </div>
      </div>

      <t-dropdown
        v-model="isOpenDropdown"
        :parent="rootRef"
        :items="dropdownItems"
        :selected-item="null"
        @select="onSelectOption"
      >
        <template #header v-if="isLoading || (!dropdownItems.length && search)">
          <li class="dropdown__item dropdown__item--hint">
            {{ isLoading ? loadingText : noResultsText }}
          </li>
        </template>
      </t-dropdown>
    </div>
    <t-errors :errors="errors" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { TDropdown, TErrors } from '@shared/ui'

const props = defineProps({
  label:          { type: String,            default: '' },
  required:       { type: Boolean,           default: false },
  options:        { type: [Array, Function], default: () => [] },
  disabled:       { type: Boolean,           default: false },
  placeholder:    { type: String,            default: '' },
  modelValue:     { type: Array,             default: () => [] },
  delay:          { type: Number,            default: 300 },
  noResultsText:  { type: String,            default: 'Нет результатов' },
  loadingText:    { type: String,            default: 'Загрузка...' },
  errors:         { type: Array,             default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'select'])

const isAsync = computed(() => typeof props.options === 'function')

const rootRef         = ref(null)
const searchRef       = ref(null)
const search          = ref('')
const isOpenDropdown  = ref(false)
const isLoading       = ref(false)
const asyncResults    = ref([])
const selectedOptions = ref([...props.modelValue])
let debounceTimer     = null

const filteredSelectedOptions = computed(() =>
  selectedOptions.value.filter(o => !o._destroy)
)

const staticOptions = computed(() => {
  if (isAsync.value) return []
  return props.options.filter(option => {
    const alreadySelected = selectedOptions.value.some(
      sel => sel.value == option.value && !sel._destroy
    )
    const matchesSearch = search.value
      ? option.text?.toLowerCase().includes(search.value.toLowerCase())
      : true
    return !alreadySelected && matchesSearch
  })
})

const filteredAsyncResults = computed(() =>
  asyncResults.value.filter(option =>
    !selectedOptions.value.some(sel => sel.value == option.value && !sel._destroy)
  )
)

const dropdownItems = computed(() =>
  isAsync.value ? filteredAsyncResults.value : staticOptions.value
)

const runSearch = async (query) => {
  if (!query) { asyncResults.value = []; return }
  isLoading.value = true
  try {
    asyncResults.value = await props.options(query) || []
  } catch (e) {
    console.error('[TSelectMultiple] async options error:', e)
    asyncResults.value = []
  } finally {
    isLoading.value = false
  }
}

const onSearchInput = () => {
  if (!isAsync.value) return
  isOpenDropdown.value = true
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => runSearch(search.value), props.delay)
}

watch(() => props.modelValue, (val) => { selectedOptions.value = [...val] })

const onFocus = () => {
  if (isAsync.value && !asyncResults.value.length && !search.value) return
  isOpenDropdown.value = true
}

const onFieldMousedown = (event) => {
  event.preventDefault()
  if (isAsync.value && !asyncResults.value.length && !search.value) {
    searchRef.value?.focus()
    return
  }
  isOpenDropdown.value = !isOpenDropdown.value
  if (isOpenDropdown.value) searchRef.value?.focus()
}

const onBlur = () => {
  // небольшая задержка чтобы клик по dropdown успел сработать
  setTimeout(() => { isOpenDropdown.value = false }, 150)
}

const onSelectOption = (option) => {
  searchRef.value?.focus()
  const savedIndex = selectedOptions.value.findIndex(o => o.value == option.value)
  if (savedIndex === -1) {
    selectedOptions.value.push(option)
  } else {
    selectedOptions.value[savedIndex] = { ...selectedOptions.value[savedIndex], _destroy: false }
  }
  search.value = ''
  asyncResults.value = []
  emit('update:modelValue', selectedOptions.value)
  emit('select', selectedOptions.value)
}

const onRemoveOption = (value) => {
  const idx = selectedOptions.value.findIndex(o => o.value == value)
  if (idx === -1) return
  if (selectedOptions.value[idx].id) {
    selectedOptions.value[idx] = { ...selectedOptions.value[idx], _destroy: true }
  } else {
    selectedOptions.value.splice(idx, 1)
  }
  emit('update:modelValue', selectedOptions.value)
  emit('select', selectedOptions.value)
}
</script>